import type { Coding } from "@medplum/fhirtypes";
import { parseInput, recordMedicationInputSchema } from "@repo/api/schemas";
import {
  type MedicationStatementDto,
  recordMedication,
  stopMedication,
  updateMedication,
  useMedicationStatements,
} from "@repo/data-store";
import { asMedicationStatementId, asPatientId, type MedicationDosage, type PatientId } from "@repo/domain";
import { RXNORM_SYSTEM } from "@repo/fhir-systems";
import { type Editor, Extension, mergeAttributes, Node } from "@tiptap/core";
import { Fragment, type Node as PMNode, Slice } from "@tiptap/pm/model";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { type NodeViewProps, ReactNodeViewRenderer } from "@tiptap/react";
import { useEffect, useMemo, useState } from "react";
import { dosageSentence } from "@/shared/lib/medication-dosage";
import { useSmartPhrasePatient } from "../context";
import { PHRASE_LINE_CLASSES, PhraseBlock, type PhraseLineState, type SaveState } from "../phrase-block";
import { nameLevelCoding } from "./drug-search";
import {
  answersFromRecord,
  isStopped,
  type MedicationAnswers,
  MedicationForm,
  parseFrequency,
  strengthLabel,
} from "./medication-form";
import { medicationName, medicationSentence, parseDose, parseStopDate, proseDrugName } from "./medication-sentence";

type Concept = { coding?: Coding[]; text?: string };

/**
 * Dose, frequency and instructions as the record keeps them: structured when
 * they parse, the clinician's words when not. Instructions follow the dose
 * sentence after a comma, which is how reopening finds them again.
 */
function dosageOf(answers: MedicationAnswers, route: MedicationDosage["route"]) {
  const { amount, text } = parseDose(answers.dose);
  const { frequency, asNeeded, unread } = parseFrequency(answers.frequency);
  const instructions = answers.instructions.trim();
  const withInstructions = (base: string | undefined) => [base, instructions].filter(Boolean).join(", ") || undefined;
  if (text || unread) {
    const base = [text ?? (amount && `${amount.value} ${amount.unit}`), answers.frequency.trim()]
      .filter(Boolean)
      .join(" ");
    return { dosage: route ? { route } : undefined, dosageText: withInstructions(base) };
  }
  const dosage: MedicationDosage = {
    ...(amount ? { amount } : {}),
    ...(frequency ? { frequency } : {}),
    ...(asNeeded ? { asNeeded } : {}),
    // Kept even before a dose is given: the drug's form says it, and a later
    // edit has only the record to read it back from.
    ...(route ? { route } : {}),
  };
  return Object.keys(dosage).length
    ? { dosage, dosageText: withInstructions(dosageSentence(dosage)) }
    : { dosage: undefined, dosageText: withInstructions(undefined) };
}

async function conceptOf(answers: MedicationAnswers): Promise<Concept | undefined> {
  const drug = answers.drug;
  if (!drug) return undefined;
  if (drug.kind === "typed") return { text: drug.name };
  const prose = proseDrugName(drug.name);
  if (answers.strength) {
    return { coding: [answers.strength.coding], text: `${prose} ${strengthLabel(answers.strength)}` };
  }
  const coding = await nameLevelCoding(drug.name);
  return coding ? { coding: [{ ...coding, system: RXNORM_SYSTEM }], text: prose } : { text: prose };
}

function stopPeriod(answers: MedicationAnswers): { end: string } | undefined | "invalid" {
  if (!isStopped(answers)) return undefined;
  const end = parseStopDate(answers.stopped);
  return end ? { end } : "invalid";
}

/** The sentence the record will read once saved, from the answers as left. */
function sentenceOf(answers: MedicationAnswers, record: MedicationStatementDto | undefined): string {
  const drug = answers.drug;
  const name = record
    ? medicationName(record)
    : drug?.kind === "coded"
      ? `${proseDrugName(drug.name)}${answers.strength ? ` ${strengthLabel(answers.strength)}` : ""}`
      : (drug?.name ?? "");
  const route = drug?.kind === "coded" ? drug.route : record?.dosage?.route;
  const { dosage, dosageText } = dosageOf(answers, route);
  const period = stopPeriod(answers);
  return medicationSentence({
    medicationStatementId: record?.medicationStatementId ?? asMedicationStatementId("pending"),
    patientId: record?.patientId ?? asPatientId("pending"),
    status: isStopped(answers) ? "stopped" : "active",
    medication: { text: name },
    ...(dosage ? { dosage } : {}),
    ...(dosageText ? { dosageText } : {}),
    ...(period && period !== "invalid" ? { effectivePeriod: period } : {}),
  });
}

type SaveResult = { state: SaveState };

/** First save: the record this phrase will point at, under the id the phrase already holds. */
async function create(statementId: string, patientId: PatientId, answers: MedicationAnswers): Promise<SaveResult> {
  if (!answers.drug) return { state: { kind: "incomplete", message: "Needs a medication" } };
  const period = stopPeriod(answers);
  if (period === "invalid") return { state: { kind: "incomplete", message: "Stop date must be a past MM/DD/YYYY" } };
  const route = answers.drug.kind === "coded" ? answers.drug.route : undefined;
  const { dosage, dosageText } = dosageOf(answers, route);
  const medication = await conceptOf(answers);
  if (!medication) return { state: { kind: "incomplete", message: "Needs a medication" } };
  const input = parseInput(recordMedicationInputSchema, {
    id: statementId,
    patient: { resourceType: "Patient", id: asPatientId(patientId) },
    medication,
    status: isStopped(answers) ? "stopped" : "active",
    ...(dosage ? { dosage } : {}),
    ...(dosageText ? { dosageText } : {}),
    ...(period ? { effectivePeriod: period } : {}),
  });
  await recordMedication(input).isPersisted.promise;
  return { state: { kind: "saved", message: "Saved to medications" } };
}

/**
 * Later saves change only what the phrase changed, so an edit made in the
 * medication dialog since is not written back over.
 */
async function update(record: MedicationStatementDto, answers: MedicationAnswers): Promise<SaveResult> {
  const before = answersFromRecord(record);
  const wasStopped = isStopped(before);
  if (wasStopped && !isStopped(answers)) {
    return { state: { kind: "error", message: "A stopped medication can't restart here" } };
  }
  const period = stopPeriod(answers);
  if (period === "invalid") return { state: { kind: "incomplete", message: "Stop date must be a past MM/DD/YYYY" } };
  const medicationStatementId = asMedicationStatementId(record.medicationStatementId);
  const stopping = !wasStopped && isStopped(answers);
  const doseChanged =
    answers.dose !== before.dose ||
    answers.frequency !== before.frequency ||
    answers.instructions !== before.instructions;
  const stopChanged = answers.stopped !== before.stopped && period;
  if (!stopping && !doseChanged && !stopChanged) return { state: { kind: "idle" } };
  // Revised first, stopped last: a stopped medication takes no further changes.
  if (doseChanged || stopChanged) {
    const { dosage, dosageText } = dosageOf(answers, record.dosage?.route);
    await updateMedication({
      medicationStatementId,
      ...(doseChanged ? { dosage: dosage ?? {}, dosageText: dosageText ?? "" } : {}),
      ...(stopChanged ? { effectivePeriod: period } : {}),
    }).isPersisted.promise;
  }
  if (stopping) await stopMedication({ medicationStatementId }).isPersisted.promise;
  return { state: { kind: "saved", message: "Saved to medications" } };
}

/** Put the caret right after the phrase, on its own line, as at the end of any sentence. */
function leavePhrase(editor: Editor, getPos: NodeViewProps["getPos"]) {
  const pos = getPos();
  if (typeof pos !== "number") return;
  const node = editor.state.doc.nodeAt(pos);
  if (node?.type.name === "medicationPhrase")
    editor
      .chain()
      .setTextSelection(pos + node.nodeSize)
      .focus()
      .run();
}

/** Bookkeeping attrs: no undo step for them, the way the editor's own normalising never has one. */
function setAttrsQuietly(editor: Editor, getPos: NodeViewProps["getPos"], attrs: Record<string, unknown>) {
  const pos = getPos();
  if (typeof pos !== "number" || editor.state.doc.nodeAt(pos)?.type.name !== "medicationPhrase") return;
  const tr = editor.state.tr;
  for (const [key, value] of Object.entries(attrs)) tr.setNodeAttribute(pos, key, value);
  editor.view.dispatch(tr.setMeta("addToHistory", false));
}

/**
 * A node view renders when its node changes, not when the editor's editability
 * does, so the chart lock arriving would otherwise leave the phrase read-only.
 */
function useEditable(editor: Editor): boolean {
  const [editable, setEditable] = useState(editor.isEditable);
  useEffect(() => {
    const sync = () => setEditable(editor.isEditable);
    editor.on("update", sync);
    editor.on("transaction", sync);
    sync();
    return () => {
      editor.off("update", sync);
      editor.off("transaction", sync);
    };
  }, [editor]);
  return editable;
}

function MedicationPhraseView({ node, editor, getPos }: NodeViewProps) {
  const patientId = useSmartPhrasePatient();
  const editable = useEditable(editor);
  const statementId: string = node.attrs.statementId;
  const { medications, isReady } = useMedicationStatements({ patientId });
  const record = medications.find((medication) => medication.medicationStatementId === statementId);
  const [editing, setEditing] = useState<boolean>(Boolean(node.attrs.autofocus) && Boolean(patientId));
  const [startAt, setStartAt] = useState<"start" | "end">(node.attrs.autofocus === "end" ? "end" : "start");
  const [save, setSave] = useState<SaveState>({ kind: "idle" });
  // The answers last left in the blanks, so an unsaved phrase reopens as it was.
  const [draft, setDraft] = useState<MedicationAnswers | null>(null);
  // A stopped medication takes no further changes, so its phrase is read-only:
  // it can be selected and deleted, never reopened.
  const readOnly = record?.status === "stopped" || record?.status === "completed";
  const live = record ? medicationSentence(record) : undefined;
  const sentence = live ?? (node.attrs.text as string);

  // The note's saved text follows the record, so the note view and its PDF read
  // what the editor shows. Only the lock holder writes it.
  useEffect(() => {
    if (live && live !== node.attrs.text && editable) setAttrsQuietly(editor, getPos, { text: live });
  }, [live, node.attrs.text, editable, editor, getPos]);
  // Asked to open: on insertion ("start"), or by Backspace from just after it ("end").
  useEffect(() => {
    const at = node.attrs.autofocus;
    if (!at) return;
    setAttrsQuietly(editor, getPos, { autofocus: false });
    if (!patientId || !editor.isEditable || readOnly) return;
    setStartAt(at === "end" ? "end" : "start");
    setSave({ kind: "idle" });
    setEditing(true);
  }, [node.attrs.autofocus, patientId, editor, getPos, readOnly]);
  useEffect(() => {
    if (readOnly !== node.attrs.readOnly) setAttrsQuietly(editor, getPos, { readOnly });
  }, [readOnly, node.attrs.readOnly, editor, getPos]);

  /** The phrase was emptied by Backspace: the reference goes, the record stays, the cursor stays put. */
  const remove = () => {
    setEditing(false);
    const pos = getPos();
    if (typeof pos !== "number") return;
    // Back into the note only if focus has nowhere else to be: a click
    // elsewhere that left the phrase keeps its focus. Read before the phrase,
    // and the input in it, are gone.
    const elsewhere =
      document.activeElement &&
      document.activeElement !== document.body &&
      !editor.view.dom.contains(document.activeElement);
    const { doc } = editor.state;
    // Only ever this phrase: a position read while the view is settling can point elsewhere.
    if (doc.nodeAt(pos)?.attrs.statementId !== statementId) return;
    const $pos = doc.resolve(pos);
    // Alone on its line, the line goes too: the note reads as it did before ".med".
    const alone = $pos.parent.childCount === 1 && $pos.depth > 0 && $pos.node($pos.depth - 1).childCount > 1;
    const from = alone ? $pos.before() : pos;
    const to = alone ? $pos.after() : pos + node.nodeSize;
    const tr = editor.state.tr.delete(from, to);
    tr.setSelection(TextSelection.near(tr.doc.resolve(Math.min(from, tr.doc.content.size)), alone ? -1 : 1));
    editor.view.dispatch(tr);
    if (!elsewhere) editor.commands.focus();
  };

  // Saved means the server has the record: an optimistic one appears the moment
  // the save starts, so it alone would turn the bar blue too early.
  const missing = isReady && !record && !editing && save.kind === "idle";
  const failed = save.kind === "error" || save.kind === "incomplete" || missing;
  // Shown without a patient (a past visit's note, read-only): the record can't
  // be looked up, so there is no save to report and the bar is the plain quote.
  const lineState: PhraseLineState = !patientId
    ? "saved"
    : failed
      ? "failed"
      : record && save.kind !== "saving" && !editing
        ? "saved"
        : "pending";
  useEffect(() => {
    if (lineState !== node.attrs.lineState) setAttrsQuietly(editor, getPos, { lineState });
  }, [lineState, node.attrs.lineState, editor, getPos]);

  const initial = useMemo(() => draft ?? answersFromRecord(record), [draft, record]);

  const done = (answers: MedicationAnswers) => {
    // Left before a drug was named: nothing to point at, so it goes.
    if (!answers.drug && !record) {
      remove();
      return;
    }
    setEditing(false);
    setDraft(answers);
    // Written now, while this clinician holds the note: the record's own reading
    // arrives after the save, by which time the lock may have gone.
    const provisional = answers.drug ? sentenceOf(answers, record) : undefined;
    if (provisional && provisional !== node.attrs.text) setAttrsQuietly(editor, getPos, { text: provisional });
    leavePhrase(editor, getPos);
    if (!patientId) return;
    setSave({ kind: "saving" });
    const pending = record ? update(record, answers) : create(statementId, patientId, answers);
    pending
      .then(({ state }) => {
        setSave(state);
        if (state.kind === "saved" || state.kind === "idle") setDraft(null);
      })
      .catch((error: unknown) => {
        setSave({ kind: "error", message: error instanceof Error ? error.message : "Not saved" });
      });
  };

  const canEdit = Boolean(patientId) && editable && !readOnly;
  return (
    <PhraseBlock>
      {editing ? (
        <MedicationForm
          initial={initial}
          drugLocked={Boolean(record)}
          startAt={startAt}
          onDone={done}
          onRemove={remove}
        />
      ) : (
        // biome-ignore lint/a11y/useSemanticElements: the sentence is prose in a note; it only becomes a form when clicked.
        <span
          role="button"
          tabIndex={-1}
          className={canEdit ? "cursor-text" : undefined}
          onClick={() => {
            if (!canEdit) return;
            // Back to grey while it is being fixed; leaving it re-saves and re-colours it.
            setSave({ kind: "idle" });
            setStartAt("end");
            setEditing(true);
          }}
          onKeyDown={undefined}
        >
          {sentence || <span className="text-muted-foreground">Medication</span>}
        </span>
      )}
    </PhraseBlock>
  );
}

/**
 * The phrase in markdown: its record id in an opening marker, the sentence, a
 * closing marker. The markers are HTML comments, which markdown renderers hide.
 * A phrase saved before the closing marker existed runs to the end of its line.
 */
const MARKER = /^<!-- med:([0-9a-f-]{36}) -->(.*?)(?:<!-- \/med -->|(?=\n|$))/i;

/**
 * The sentence comes from typed words, which may hold "<!--" or "-->" and so
 * close the marker early: the phrase would split, and split again each save.
 */
const encodeSentence = (text: string) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const decodeSentence = (text: string) => text.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");

/** A line holding a phrase wears the quote's bar, set apart from free text, coloured by whether it is saved. */
const phraseLines = new Plugin({
  key: new PluginKey("medicationPhraseLines"),
  props: {
    decorations(state) {
      const lines: Decoration[] = [];
      state.doc.descendants((node, pos) => {
        if (!node.isTextblock) return true;
        const states: PhraseLineState[] = [];
        node.forEach((child) => {
          if (child.type.name === "medicationPhrase") states.push(child.attrs.lineState);
        });
        if (states.length) {
          const state = states.includes("failed") ? "failed" : states.every((s) => s === "saved") ? "saved" : "pending";
          lines.push(Decoration.node(pos, pos + node.nodeSize, { class: PHRASE_LINE_CLASSES[state] }));
        }
        return false;
      });
      return DecorationSet.create(state.doc, lines);
    },
  },
});

/** Each pasted phrase not in `moving` becomes its sentence as plain text; empty ones go. */
function unlinkPhrases(fragment: Fragment, moving: ReadonlySet<string>): Fragment {
  const nodes: PMNode[] = [];
  fragment.forEach((child) => {
    if (child.type.name !== "medicationPhrase") {
      nodes.push(child.isLeaf ? child : child.copy(unlinkPhrases(child.content, moving)));
    } else if (moving.has(child.attrs.statementId)) {
      nodes.push(child);
    } else if (child.attrs.text) {
      nodes.push(child.type.schema.text(child.attrs.text));
    }
  });
  return Fragment.from(nodes);
}

/**
 * A phrase is pasted as a phrase only when it is being moved within this note:
 * cut here, or dragged here. A copy would list the same medication twice, so a
 * copy, or anything from another note or another patient, comes in as the
 * sentence's words, no longer tied to the record.
 */
function phrasePaste(): Plugin {
  const moving = new Set<string>();
  const remember = (view: EditorView) => {
    moving.clear();
    const { from, to } = view.state.selection;
    view.state.doc.nodesBetween(from, to, (node) => {
      if (node.type.name === "medicationPhrase") moving.add(node.attrs.statementId);
    });
    return false;
  };
  return new Plugin({
    key: new PluginKey("medicationPhrasePaste"),
    props: {
      handleDOMEvents: {
        cut: remember,
        dragstart: remember,
        copy: () => {
          moving.clear();
          return false;
        },
      },
      transformPasted(slice, view) {
        // An Alt-drag copies rather than moves.
        const copying = view.dragging && !view.dragging.move;
        const content = unlinkPhrases(slice.content, copying ? new Set() : moving);
        // A cut moves once: pasting it again would be a copy.
        moving.clear();
        return new Slice(content, slice.openStart, slice.openEnd);
      },
    },
  });
}

/**
 * A medication phrase: a reference to the MedicationStatement it created, shown
 * as a sentence read live off that record. The note keeps the sentence too, so
 * anything reading the note's text reads the phrase.
 *
 * Inline, alone on its line, rather than a block: a click past the end of the
 * sentence then lands the caret after it, the way it would after any text.
 */
export const MedicationPhrase = Node.create({
  name: "medicationPhrase",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      statementId: { default: null, parseHTML: (element) => element.getAttribute("data-statement-id") },
      text: { default: "", parseHTML: (element) => element.textContent ?? "" },
      // Set on insertion only, so the new phrase opens with its first blank focused.
      autofocus: { default: false, rendered: false },
      // Where the record's save stands; drives the bar's colour. Never saved into the note.
      lineState: { default: "pending", rendered: false },
      // The record is stopped: Backspace selects the phrase instead of opening it.
      readOnly: { default: false, rendered: false },
    };
  },
  parseHTML() {
    return [{ tag: "span[data-medication-phrase]" }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, { "data-medication-phrase": "", "data-statement-id": node.attrs.statementId }),
      node.attrs.text,
    ];
  },
  renderText({ node }) {
    return node.attrs.text;
  },
  markdownTokenizer: {
    name: "medicationPhrase",
    level: "inline",
    start: (src: string) => src.indexOf("<!-- med:"),
    tokenize(src: string) {
      const match = MARKER.exec(src);
      if (!match) return undefined;
      return {
        type: "medicationPhrase",
        raw: match[0],
        statementId: match[1],
        text: decodeSentence((match[2] ?? "").trim()),
      };
    },
  },
  parseMarkdown(token) {
    return { type: "medicationPhrase", attrs: { statementId: token.statementId, text: token.text } };
  },
  renderMarkdown(node) {
    return `<!-- med:${node.attrs?.statementId} -->${encodeSentence(node.attrs?.text ?? "")}<!-- /med -->`;
  },
  addProseMirrorPlugins() {
    return [phraseLines, phrasePaste()];
  },
  addKeyboardShortcuts() {
    return {
      // Backspace from just after a phrase steps into it, at its end, rather than
      // deleting it whole: one stray key must not drop a phrase from the note.
      Backspace: ({ editor }) => {
        const { selection } = editor.state;
        const before = selection.empty ? selection.$from.nodeBefore : null;
        if (before?.type.name !== this.name) return false;
        const pos = selection.from - before.nodeSize;
        // Selected first, so a second Backspace deletes it, as with any atom.
        if (before.attrs.readOnly) return editor.commands.setNodeSelection(pos);
        editor.view.dispatch(editor.state.tr.setNodeAttribute(pos, "autofocus", "end").setMeta("addToHistory", false));
        return true;
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(MedicationPhraseView);
  },
});

/**
 * A line opening with a phrase marker is a paragraph, not raw HTML. Markdown
 * reads a line starting "<!--" as an HTML block, which would drop the marker
 * and the phrase's link to its record with it; claiming the line first hands it
 * to the inline phrase parser above.
 */
export const MedicationPhraseLine = Extension.create({
  name: "medicationPhraseLine",
  markdownTokenizer: {
    name: "medicationPhraseLine",
    level: "block",
    // Never clips a paragraph: marked tries a block tokenizer at every block
    // anyway, and a clipped paragraph makes it merge the paragraph after the
    // phrase into the phrase's token, dropping the text before and repeating the
    // phrase. Left out, Tiptap supplies a start that breaks inline parsing.
    start: () => -1,
    tokenize(src, _tokens, lexer) {
      const match = /^(<!-- med:[0-9a-f-]{36} -->[^\n]*)(?:\n|$)/i.exec(src);
      if (!match?.[1]) return undefined;
      return { type: "paragraph", raw: match[0], text: match[1], tokens: lexer.inlineTokens(match[1]) };
    },
  },
});
